import React from 'react';

/**
 * Badge color map for market_structure, outlook, momentum, and risk_level.
 */
const badgeConfig = {
  // market_structure
  uptrend: { bg: 'bg-green-100 dark:bg-green-900/40', text: 'text-green-700 dark:text-green-300' },
  downtrend: { bg: 'bg-red-100 dark:bg-red-900/40', text: 'text-red-700 dark:text-red-300' },
  ranging: { bg: 'bg-yellow-100 dark:bg-yellow-900/40', text: 'text-yellow-700 dark:text-yellow-300' },
  // outlook
  strong_bullish: { bg: 'bg-emerald-200 dark:bg-emerald-800/60', text: 'text-emerald-900 dark:text-emerald-100' },
  bullish: { bg: 'bg-green-100 dark:bg-green-900/40', text: 'text-green-700 dark:text-green-300' },
  neutral: { bg: 'bg-gray-100 dark:bg-gray-700/40', text: 'text-gray-700 dark:text-gray-300' },
  bearish: { bg: 'bg-red-100 dark:bg-red-900/40', text: 'text-red-700 dark:text-red-300' },
  strong_bearish: { bg: 'bg-rose-200 dark:bg-rose-800/60', text: 'text-rose-900 dark:text-rose-100' },
  // momentum
  strong: { bg: 'bg-green-100 dark:bg-green-900/40', text: 'text-green-700 dark:text-green-300' },
  moderate: { bg: 'bg-yellow-100 dark:bg-yellow-900/40', text: 'text-yellow-700 dark:text-yellow-300' },
  weak: { bg: 'bg-orange-100 dark:bg-orange-900/40', text: 'text-orange-700 dark:text-orange-300' },
  // risk_level
  low: { bg: 'bg-green-100 dark:bg-green-900/40', text: 'text-green-700 dark:text-green-300' },
  medium: { bg: 'bg-yellow-100 dark:bg-yellow-900/40', text: 'text-yellow-700 dark:text-yellow-300' },
  high: { bg: 'bg-red-100 dark:bg-red-900/40', text: 'text-red-700 dark:text-red-300' },
};

const Badge = ({ label, value }) => {
  if (!value) return null;
  const key = String(value).toLowerCase();
  const colors = badgeConfig[key] || { bg: 'bg-gray-100 dark:bg-gray-700/40', text: 'text-gray-700 dark:text-gray-300' };
  const displayValue = String(value).includes('_')
    ? (() => {
        const normalized = String(value).replace(/_/g, ' ').toLowerCase();
        return normalized.charAt(0).toUpperCase() + normalized.slice(1);
      })()
    : value;
  return (
    <div className="flex flex-col items-center gap-1">
      <span className="text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-400 font-medium">{label}</span>
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${colors.bg} ${colors.text}`}>
        {displayValue}
      </span>
    </div>
  );
};

/**
 * Format a date string/Date to "Mon DD" (e.g. "Jan 15").
 * Returns null when the input is falsy or unparseable.
 */
export const formatHeadingDate = (raw) => {
  if (!raw) return null;
  // Backend serves UTC datetimes that may lack a timezone suffix (naive),
  // which JS would interpret as local time.  Append 'Z' so getUTC* is correct.
  let input = raw;
  if (typeof input === 'string' && !/Z|[+-]\d{2}:?\d{2}$/.test(input)) {
    input += 'Z';
  }
  const d = new Date(input);
  if (isNaN(d.getTime())) return null;
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[d.getUTCMonth()]} ${String(d.getUTCDate()).padStart(2, '0')}`;
};

const DailyComment = ({ data, createdAt, theme = 'light' }) => {
  const dateSuffix = formatHeadingDate(createdAt);
  const heading = dateSuffix ? `Technical Outlook — ${dateSuffix}` : 'Technical Outlook';
  const isEmpty = !data || (typeof data === 'object' && Object.keys(data).length === 0);
  const infoText = 'Classification labels produced by a fixed technical-analysis methodology on historical data — not predictions of certainty or trading recommendations.';

  const headingSection = (
    <div className="mb-3 flex items-center justify-center gap-2 text-center">
      <h3 className="text-lg font-semibold">{heading}</h3>
      <span className="relative inline-flex items-center group">
        <button
          type="button"
          aria-label="technical outlook info"
          title="Technical outlook info"
          className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full border border-gray-300 text-[10px] font-semibold leading-none text-gray-600 hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100"
        >
          i
        </button>
        <div className="pointer-events-none absolute left-0 top-full z-10 mt-2 w-72 rounded-md border border-gray-200 bg-white px-2 py-1 text-[12px] leading-snug text-gray-700 opacity-0 shadow-sm transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200">
          {infoText}
        </div>
      </span>
    </div>
  );

  if (isEmpty) {
    return (
      <div
        className={`rounded-lg border mt-4 p-4 ${
          theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}
      >
        {headingSection}
        <p className="text-sm text-gray-500 dark:text-gray-400 italic">Update in progress ...</p>
      </div>
    );
  }

  const {
    market_structure,
    outlook,
    risk_level,
    momentum,
    key_observations,
    rationale,
  } = data;

  const hasAnyBadge = market_structure || outlook || risk_level || momentum;

  return (
    <div
      className={`rounded-lg border mt-4 p-4 ${
        theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}
    >
      {headingSection}

      {/* Badges row */}
      {hasAnyBadge && (
        <div className="flex flex-wrap gap-4 mb-4">
          <Badge label="Outlook" value={outlook} />
          <Badge label="Structure" value={market_structure} />
          <Badge label="Momentum" value={momentum} />
          <Badge label="Risk" value={risk_level} />
        </div>
      )}

      {/* Rationale */}
      {rationale && (
        <p className="mt-3 text-sm text-gray-600 dark:text-gray-400 leading-relaxed text-left">
          {rationale}
        </p>
      )}

      {/* Key observations */}
      {Array.isArray(key_observations) && key_observations.length > 0 && (
        <div className="mt-3">
          <span className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 font-medium">
            Key Observations
          </span>
          <ul className="mt-1 list-disc list-inside space-y-1">
            {key_observations.map((obs, i) => (
              <li key={i} className="text-sm text-gray-600 dark:text-gray-400 text-left">
                {obs}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default DailyComment;
